'use client';

import { Box, Grid, Link as MuiLink, Typography, IconButton } from "@mui/material";
import { Link } from "@/i18n/routing";
import { useRef } from "react";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { NewsArticleEntry } from "@/types/contentful";
import { useTranslations } from "next-intl";

interface NewsSectionProps {
    newsItems?: NewsArticleEntry[];
}

const NewsSection = ({ newsItems = [] }: NewsSectionProps) => {
    const t = useTranslations('HomePage.news');

    // Display all news items
    const displayNews = newsItems;
    const scrollContainerRef = useRef<HTMLDivElement>(null);


    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const isCarousel = displayNews.length > 3;

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = 350;
            scrollContainerRef.current.scrollBy({
                left: direction === 'right' ? scrollAmount : -scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <Box
            sx={{
                py: 8,
                backgroundColor: "#ffffff",
            }}
        >
            <Grid container sx={{ maxWidth: "lg", margin: "auto", px: { xs: 2.5, md: 0 } }}>
                <Grid size={12} sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#132c4b' }}>
                        {t(`title`)}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {isCarousel && (
                            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                                <IconButton
                                    onClick={() => scroll('left')}
                                    sx={{
                                        border: '1px solid #e2e8f0',
                                        mr: 1,
                                        '&:hover': { backgroundColor: '#f8fafc' }
                                    }}
                                    aria-label="Previous"
                                >
                                    <ChevronLeftIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => scroll('right')}
                                    sx={{
                                        border: '1px solid #e2e8f0',
                                        '&:hover': { backgroundColor: '#f8fafc' }
                                    }}
                                    aria-label="Next"
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            </Box>
                        )}
                        <MuiLink href="/news" sx={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.9rem', '&:hover': { textDecoration: 'underline' } }}>
                            {t(`view_all`)}
                        </MuiLink>
                    </Box>
                </Grid>

                <Grid
                    ref={scrollContainerRef}
                    container
                    size={12}
                    spacing={3}
                    sx={isCarousel ? {
                        display: 'flex',
                        flexWrap: 'nowrap',
                        overflowX: 'auto',
                        pb: 2,
                        WebkitOverflowScrolling: 'touch',
                        scrollSnapType: 'x mandatory',
                        '&::-webkit-scrollbar': { display: 'none' },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                    } : {}}
                >
                    {displayNews.length > 0 ? (
                        displayNews.map((item, index) => {
                            const { title, publishedDate, type, category, sourceLink } = item.fields;
                            const id = item.sys.id;
                            const displayCategory = type || category || 'ニュース';
                            const date = formatDate(publishedDate);
                            const href = sourceLink || `/news/${id}`;
                            const isExternal = !!sourceLink;

                            return (
                                <Grid size={isCarousel ? { xs: 10, md: 4 } : { xs: 12, md: 4 }} key={index} sx={isCarousel ? { flex: '0 0 auto', scrollSnapAlign: 'start' } : {}}>
                                    <Link href={href} target={isExternal ? "_blank" : undefined} style={{ textDecoration: 'none', height: '100%', display: 'block', width: '100%' }}>
                                        <div className="bg-white p-6 rounded-xl border border-slate-200 hover:shadow-md transition-shadow duration-200 h-full flex flex-col items-start w-full">
                                            <div className="flex items-center gap-3 mb-3">
                                                <span suppressHydrationWarning className="text-slate-500 text-sm font-medium">{date}</span>
                                                <span className="bg-slate-100 text-slate-600 text-xs px-2.5 py-1 rounded-full font-medium">
                                                    {displayCategory}
                                                </span>
                                            </div>
                                            <h3 className="text-[#132c4b] font-bold text-base leading-relaxed line-clamp-3">
                                                {title}
                                            </h3>
                                        </div>
                                    </Link>
                                </Grid>
                            )
                        })
                    ) : (
                        <Grid size={12}>
                            <Typography variant="body1" sx={{ color: '#64748b', textAlign: 'center' }}>
                                {t(`no_news`)}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default NewsSection;